import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { BackLink } from "./back-link";
import type { NavigationLink } from "./navigation-dialog";

/** The thing being viewed, separate from the permanent global header. Names
 * wrap here instead of competing with account and language controls. */
export function PageContext({
  title,
  titleAs: Title = "div",
  description,
  titleHref,
  titleArrow = false,
  eyebrow,
  lead,
  backHref,
  backLabel,
  historyBack = true,
  parent,
  status,
  meta,
  actions,
  links = [],
}: {
  title: string;
  titleAs?: "div" | "h1";
  description?: ReactNode;
  titleHref?: string;
  titleArrow?: boolean;
  eyebrow: string;
  lead?: ReactNode;
  backHref?: string | undefined;
  backLabel?: string | undefined;
  historyBack?: boolean;
  /** A named parent remains available even when Back follows a different page. */
  parent?: NavigationLink;
  status?: ReactNode;
  /**
   * A short state for the kind of page (the board's lifecycle badge,
   * 2026-09-15). ONE element placed by grid: beside the eyebrow on a phone,
   * where a row of its own pushed the content down, and right of the title
   * from `adm`, as `status` sits. Not combined with `lead`.
   */
  meta?: ReactNode;
  actions?: ReactNode;
  links?: NavigationLink[];
}) {
  const t = useT();
  return (
    <div className="space-y-3">
      {(backHref !== undefined || parent !== undefined) && (
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
          {backHref !== undefined && (
            <BackLink href={backHref} label={backLabel ?? t("common.back")} history={historyBack} />
          )}
          {parent && (
            <Link
              to={parent.href}
              className="min-w-0 break-words py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {parent.label}
            </Link>
          )}
        </div>
      )}
      <div className="flex flex-col gap-3 adm:flex-row adm:items-center adm:justify-between">
        <div className={cn("flex min-w-0 items-center gap-3", meta != null && "adm:flex-1")}>
          {lead}
          <div
            className={cn(
              "min-w-0",
              meta == null
                ? "space-y-1"
                : "grid flex-1 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2.5 gap-y-1 adm:grid-cols-[minmax(0,1fr)_auto] adm:gap-x-6",
            )}
          >
            <div
              className={cn(
                "eyebrow text-muted-foreground",
                meta != null && "col-start-1 row-start-1",
              )}
            >
              {eyebrow}
            </div>
            {meta != null && (
              <div className="col-start-2 row-start-1 flex min-w-0 adm:row-end-3">{meta}</div>
            )}
            <Title
              className={cn(
                "nxt break-words text-[1.75rem] leading-tight text-pretty uppercase adm:text-[2.5rem]",
                meta != null && "col-start-1 col-end-3 adm:col-end-2",
              )}
            >
              {titleHref ? (
                <Link
                  to={titleHref}
                  className="decoration-muted-foreground underline-offset-4 hover:underline"
                >
                  {title}
                  {titleArrow && (
                    <ArrowUpRightIcon
                      className="ml-2 inline size-5 align-baseline adm:size-6"
                      aria-hidden="true"
                    />
                  )}
                </Link>
              ) : (
                title
              )}
            </Title>
            {description && (
              <div
                className={cn(
                  "pt-1 text-sm text-muted-foreground",
                  meta != null && "col-start-1 col-end-3 adm:col-end-2",
                )}
              >
                {description}
              </div>
            )}
          </div>
        </div>
        {(meta == null || status != null || actions != null || links.length > 0) && (
          <div
            className={cn(
              "shrink-0 flex-wrap items-center gap-x-4 gap-y-2",
              status != null || actions != null ? "flex" : "hidden adm:flex",
            )}
          >
            {status}
            {actions}
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="hidden items-center gap-1 py-2 text-sm font-medium text-muted-foreground hover:text-foreground adm:inline-flex"
                {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              >
                {link.label}
                {link.external && <ArrowUpRightIcon className="size-3.5" aria-hidden="true" />}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
